import { type FunctionComponent } from "react";
import { useLegalStore } from "@stores/legal";
import "./TermsAndServices.scss";

export declare type TermsAndServicesProps = {};

export const TermsAndServices: FunctionComponent<
  TermsAndServicesProps
> = ({}) => {
  const { scrolled_terms, setScrolledTerms } = useLegalStore();

  const classes = () =>
    [`terms-and-services`, scrolled_terms ? "complete" : null]
      .filter((v) => v)
      .join(" ");

  const onScroll = (evt: any) => {
    const { scrollHeight, scrollTop, clientHeight } = evt.target;
    const scrolledHeight = scrollTop + clientHeight;
    if (scrolledHeight === scrollHeight || scrolledHeight > scrollHeight - 10) {
      setScrolledTerms(true);
    }
  };

  return (
    <div className={classes()}>
      <section className="terms-of-service legal" onScroll={onScroll}>
        {/* <h1 className="centered">Terms of Service</h1> */}
        <p className="dated">Effective Date Jul 29, 2024</p>

        <p>
          Welcome to Bracket. These Terms of Service (“Terms”) are between you
          (also referred to herein as the (“User,” “you,” or “your”) and Bracket
          Labs Group, SA. (the “Company,” “we,” “us,” or “our”), a company
          organized under the laws of Panama. These Terms govern your access to
          and use of the Company’s products and services, including, without
          limitation, (i) our website(s), (ii) our platform and services, and
          (iii) our stand-alone application known as “Bracket,” (collectively,
          items (i) through (iii) above are referred to as the “Bracket
          Products”). Our provision of the Bracket Products, and granting you
          access thereto, and the ancillary services we provide in connection
          therewith are collectively referred to herein as the “Services.”
        </p>

        <p>
          <b>
            Please note that the Company does not offer the Bracket Products or
            Services to persons or entities (i) who reside in, are located in,
            are citizens of, or incorporated in or have a registered office in
            (a) countries where the use of Bracket Products or Services or
            distribution, sale, or purchase of digital or crypto assets may be
            deemed illegal or subject to regulatory licensure unless otherwise
            noted (b) the United States of America (“US Persons”), UK, Panama,
            Canada, Iran, North Korea, Cuba, Syria, Myanmar (Burma), Crimea,
            Donetsk, Luhansk, or any country or territory subject to
            country-wide or region-wide economic sanctions by the US
            (“Restricted Territories”), and/or (ii) on lists maintained by US
            Office of Foreign Assets Control (OFAC) or US Dept. of Treasury
            (collectively, “Restricted Users”). If you are a Restricted User,
            you are prohibited from using or accessing the Bracket Products or
            Services. USE OF A VIRTUAL PRIVATE NETWORK (“VPN”) OR ANY OTHER
            METHOD TO CIRCUMVENT THE RESTRICTIONS SET FORTH HEREIN IS STRICTLY
            PROHIBITED.
          </b>
        </p>

        <p>
          By clicking “I Accept” or accessing the Bracket Products or Services,
          you acknowledge that you have read, understood, and agree to be
          legally bound by these Terms, and the terms and conditions of our
          Privacy Policy (the “Privacy Policy”), which is hereby incorporated by
          reference and made a part of these Terms (the Terms, together with the
          Privacy Policy are collectively referred to herein as the
          “Agreement”). If you do not agree to the Agreement, you may not access
          the Bracket Products and Services and must immediately cease any use
          of the Bracket Products or Services. We may amend or update the
          Agreement from time to time by posting a revised or updated version of
          this Agreement on our website and/or sending a notice to the email
          associated with your account (if applicable) and may do so without
          notice to you. If you access or use the Bracket Products or Services
          after the Effective Date set forth in the Agreement, you will be
          deemed to have agreed to such terms. Accordingly, please review the
          Agreement posted at this location periodically. It is your sole
          responsibility to review the Agreement from time to time to view such
          changes and to ensure that you understand the terms and conditions
          that apply when you access or use the Bracket Products or Services.
          Each time you use the Bracket Products or Services, you agree to be
          bound by the terms of the Agreement in effect at the time of your use
          thereof.
        </p>

        <p>
          THIS AGREEMENT CONTAINS IMPORTANT PROVISIONS THAT MAY AFFECT YOUR
          LEGAL RIGHTS, INCLUDING AN ARBITRATION PROVISION THAT REQUIRES YOU AND
          THE COMPANY TO RESOLVE ALL DISPUTES BY BINDING ARBITRATION INSTEAD OF
          A COURT. PLEASE SEE THE SECTIONS BELOW TITLED “BINDING ARBITRATION”
          AND “CLASS ACTION WAIVER.” EXCEPT AS PERMITTED BY THE FOREGOING
          SECTIONS, BY ACCESSING THE BRAKET PRODUCTS OR SERVICES, YOU WAIVE YOUR
          RIGHT TO TRIAL BY JURY, AND YOU WILL NOT BE ABLE TO BRING ANY CLAIMS
          IN FEDERAL OR STATE COURT. PLEASE READ THIS AGREEMENT CAREFULLY.‍
        </p>

        <h5>1. Eligibility.</h5>
        <p>
          The Bracket Products and Services are available only for individuals
          aged 18 years or older. If you are under 18, please do not use the
          Bracket Products or Services. By accessing and/or using the Bracket
          Products or Services, you represent and warrant that you are at least
          18 and that you are not a Restricted User. We are under no obligation
          to accept any individual as a User of the Bracket Products or Services
          and may accept or reject any User at our sole and complete discretion.
          In addition, we may but are not required to deactivate any account at
          any time, including, without limitation, if we determine that you have
          violated this Agreement.
        </p>

        <h5>2. Connecting Your Wallet.</h5>
        <p>
          To access and use the Bracket Products and Services, Users may be
          required to connect their digital wallet(s) (each, a “Wallet”) to the
          Bracket Product(s). By connecting your Wallet to a Bracket Product,
          each User represents and warrants to the Company the following: (a)
          User is an individual who is at least eighteen (18) years of age; (b)
          all information provided in connection with such User’s Wallet is
          correct and complete; (c) User shall not conduct any activity through
          the Bracket Products or Service that violates any applicable law; and
          (d) User is not a Restricted User and shall not re-sell, gift, or
          otherwise transfer a digital asset to a Restricted User.
        </p>
        <p>
          When you link a Wallet to a Bracket Product, you understand and agree
          that you are solely responsible for maintaining the security of your
          Wallet and your control over any Wallet-related authentication
          credentials, private or public cryptocurrency keys, and digital assets
          or cryptocurrencies that are stored in or are accessible through your
          Wallet. Any unauthorized access to your Wallet by third parties could
          result in the loss or theft of digital assets and/or funds held in
          your Wallet and any associated wallets, including any linked financial
          information such as bank account(s) or credit card(s). The Company is
          not responsible for managing and maintaining the security of your
          Wallet, and we bear no responsibility or liability to you for any
          unauthorized access to or use of your Wallet or if you are unable to
          locate your credentials. If you notice any unauthorized or suspicious
          activity in your Wallet that seems to be related to the Bracket
          Products or Services, please notify us immediately at{" "}
          <a href="mailto:contact@bracket.fi">contact@bracket.fi</a>
        </p>
        <p>
          Transactions which take place using the Bracket Products and Services
          are typically managed and confirmed via one or more blockchain-based
          networks, including but not limited to the Arbitrum network, a Layer 2
          optimistic rollup technology whose sequencer posts transactions to the
          Ethereum network, the Binance Smart Chain, and the Ethereum network
          (collectively, the “Blockchain Network(s)”). You understand that
          transactions occurring on the Blockchain Networks are public and that
          your Blockchain Network public address will be made publicly visible
          whenever you engage in a transaction on a Bracket Product. Further,
          you understand that we have no control over transactions on the
          Blockchain Networks. By using the Bracket Products and Services, you
          acknowledge and agree that: (i) we do not have the ability to cancel
          or otherwise modify any transaction; and (ii) the applicable
          blockchain network may fail to complete any transaction.
        </p>
        <p>
          The Bracket Products and Services are non-custodial. When you deposit
          digital assets into your Wallet connected to the Bracket Products, you
          retain control over those digital assets at all times. The private
          key(s) associated with the Blockchain Network address from which you
          transfer digital assets are the only private key(s) that can control
          the digital assets in such Wallet. In some cases, you may withdraw
          digital assets from your Wallet only to the Blockchain Network address
          from which you deposited the digital assets. You own all rights,
          title, and interest in and to any digital assets held in your Wallet.
          You hereby represent and warrant to us that: (i) any digital assets
          that you transfer into your Wallet using a Bracket Product are owned
          exclusively by you at the time of transfer; and (ii) you are
          authorizing us to carry out transactions relating to your Wallet, and
          all transactions on the Bracket Products initiated with your Wallet
          are for your own account and not on behalf of any other person or
          entity.
        </p>

        <h5>3. Fees and Price Estimates.</h5>
        <p>
          You are required to pay all fees associated with your interactions
          with, and transactions that occur on, the Blockchain Network,
          including “gas” costs, as well as all other fees reflected on the
          Bracket Products at the time of your use thereof. Fees provided
          through the Bracket Products may be estimates; actual fees may vary.
        </p>
        <p>
          By placing an order for digital assets using the Bracket Products and
          Services, you agree that you are submitting a binding offer to
          purchase such amounts of digital assets, you agree to pay all
          applicable fees associated with the purchase, and you authorize the
          Company to automatically charge and collect such fees from your
          Wallet.
        </p>
        <p>
          You will be solely responsible for paying any and all VAT, taxes,
          duties, and assessments (except taxes on our net income) now or
          hereafter claimed or imposed by any governmental authority associated
          with your purchase of digital assets using the Bracket Products or
          Services (collectively, the “Taxes”). You hereby agree that you: (a)
          will pay or reimburse us for all Taxes of any jurisdiction, including
          value added Taxes and Taxes as required by international tax treaties,
          customs or other import or export taxes, and amounts levied in lieu
          thereof based on charges set, services performed or payments made
          hereunder, as are now or hereafter may be imposed under the authority
          of any national, state, local or any other taxing jurisdiction; and
          (b) will not be entitled to deduct the amount of any such taxes,
          duties or assessments from payments made to us pursuant to this
          Agreement.
        </p>
        <p>
          The Bracket Products rely on third-party sources to determine the
          prices of digital assets, including oracle feeds and keeper contracts.
          All prices stated in a Bracket Product are estimates, and the Company
          is not responsible or liable for inaccurate pricing information due to
          circumstances beyond our reasonable control, including, without
          limitation, unavailability of an oracle feed, keeper contract, or the
          Blockchain Network. Prices and pricing information may be higher or
          lower than prices available on platforms providing similar services.
        </p>

        <h5>4. Staking Services.</h5>
        <p>
          The Company provides a front-end interface that displays data that
          facilitates users' interfacing with a set of decentralized
          open-sourced smart contracts that allow for the staking and restaking
          of certain digital assets. Your use of such smart contracts is
          entirely at your own risk. YOU ARE NOT REQUIRED TO STAKE IN ORDER TO
          USE THE BRACKET PRODUCTS OR SERVICES AND YOU CAN REQUEST TO UNSTAKE AT
          ANY TIME. FOR MORE INFORMATION PLEASE REVIEW THE RELEVANT
          DOCUMENTATION, HERE. If you choose to stake your digital assets and/or
          Rewards (as defined herein), the Company may perform certain
          blockchain operations involving your digital assets and Rewards.
          Staking your digital assets and/or Rewards does not affect the
          ownership of your digital assets in any way. Unless otherwise
          specified, the “staking rewards rate” disclosed by the Company for a
          particular supported digital asset is an estimate that is subject to
          change over time. THE COMPANY DOES NOT GUARANTEE THAT YOU WILL RECEIVE
          ANY STAKING REWARDS. Additionally, the third-party technologies
          required to stake your digital assets and/or Rewards, including but
          not limited to the open-sourced smart contracts and Wallets, are not
          part of the Bracket Products or Services, and your use of such
          third-party tools is entirely at your own risk.
        </p>

        <h5>5. Community Guidelines.</h5>
        <p>
          By accessing and/or using the Bracket Products or Services, you agree
          to comply with the following community guidelines (the “Community
          Guidelines”):
        </p>
        <ul>
          <li>
            You will only access the Bracket Products and Services if you are
            eighteen (18) years of age or older and are not a Restricted User;
          </li>
          <li>
            You will not use or access the Bracket Products or Services from a
            jurisdiction (including an IP address in a jurisdiction) that we
            have, in our sole discretion, determined is a jurisdiction where the
            use of the Bracket Products and Services is prohibited, including
            the United States of America or any Restricted Territory;
          </li>
          <li>
            You will comply with all applicable laws relating to your use of the
            Bracket Products and Services and will not use the Bracket Products
            or Services for any unlawful, deceptive, or fraudulent purpose;
          </li>
          <li>
            You will not “stalk,” threaten, or otherwise harass another person;
          </li>
          <li>
            You will not access or use the Bracket Products or Services to
            collect any market research for a competing business;
          </li>
          <li>
            You will not access or use the Bracket Products or Services to
            collect to scrape, crawl, data mine, or otherwise extract data via
            an automated means (including but not limited to using robots,
            botnets, or other scrapers) or otherwise for any purpose whatsoever;
          </li>
          <li>
            You will not access, reproduce, download, distribute, transmit,
            broadcast, display, sell, license, alter, modify, or otherwise use
            any part of the Bracket Products or Services except as expressly
            authorized by the Company;
          </li>
          <li>
            You will not impersonate any person or entity or falsely state or
            otherwise misrepresent your affiliation with a person or entity;
          </li>
          <li>
            You will not engage in improper or abusive trading practices,
            including (i) any fraudulent act or scheme to defraud, deceive,
            trick or mislead; (ii) trading ahead of another User of the Bracket
            Products or Services, or front-running; (iii) accommodation trading;
            (iv) fictitious transactions; (v) pre-arranged or non-competitive
            transactions; (vi) violations of bids or offers; (vii) spoofing or
            other market manipulation; or (viii) entering orders for the purpose
            of entering into transactions without a net change in either party’s
            open positions but a resulting profit to one party and a loss to the
            other party, commonly known as a “money pass;”
          </li>
          <li>
            You will not interfere with or attempt to interrupt the proper
            operation of the Bracket Products or Services through the use of any
            virus, device, information collection or transmission mechanism,
            software or routine, or access or attempt to gain access to any
            data, files, or passwords related to the Bracket Products or
            Services through hacking, password or data mining, or any other
            means;
          </li>
          <li>
            You will not cover, obscure, block, or in any way interfere with any
            advertisements and/or safety features (e.g., report abuse button) on
            the Bracket Products or Services;
          </li>
          <li>
            You will not use any artificially intelligent agent (AI Agent),
            robot, or other automated means to access the Bracket Products or
            Services or any digital asset for any purpose without our express
            written permission;
          </li>
          <li>
            You will not take any action that imposes or may impose (in our sole
            discretion) an unreasonable or disproportionately large load on our
            technical infrastructure;
          </li>
          <li>
            You will let us know about inappropriate content of which you become
            aware; and
          </li>
          <li>
            You will not take any action that otherwise involves or results in
            the wrongful seizure or receipt of any digital assets, including
            purchase or sale of digital assets by illegal means.
          </li>
        </ul>

        <p>
          We have the right, but not the obligation, to review and reject or
          remove content that, in our sole discretion, does not adhere to these
          Community Guidelines. We also reserve the right, in our sole and
          absolute discretion, to deny you access to any website, or to any
          portion of any website, for any reason with or without notice. We may,
          at our sole and absolute discretion, without notice or liability to
          you, and without limiting any of our other rights or remedies at law
          or in equity, immediately suspend or terminate your user account.
        </p>

        <h5>6. Our Content</h5>
        <p>
          Apart from digital assets, the Bracket Products and Services may
          contain material, such as software, data, text, graphics, images,
          designs, sound recordings, audiovisual works, information, and other
          material provided by or on behalf of the Company, including artwork
          (collectively referred to as the “Content”). You shall not (i) sell,
          transfer, assign, license, sublicense, or modify the Bracket Products,
          Services or Content; (ii) reproduce, display, publicly perform, make a
          derivative version of, distribute, reverse engineer or otherwise
          attempt to discover the source code of any software contained in or
          relating to the Bracket Products, Services, or Content; or (iii)
          otherwise use the Bracket Products, Services, or Content in any way
          for any public or commercial purpose or in any manner other than as
          expressly permitted in this Agreement. We retain all right, title and
          interest and in and to (x) the Bracket Products and Services, and all
          Content therein or thereon; (y) all improvements and modifications to,
          and derivative works of, the Bracket Products, Services, and all
          Content therein or thereon; and (z) all intellectual property rights
          relating to the foregoing. Access to and use of the Bracket Products
          or Services does not confer any rights to the Bracket Products or
          Services, or any Content, except for the limited right to access and
          use the Bracket Products or Services, and Content as expressly
          permitted herein.
        </p>
        <p>
          The trademarks, service marks, and logos used and displayed on the
          Bracket Products and Services may be registered and/or unregistered
          trademarks or service marks of the Company of our licensors
          (collectively, the “Trademarks”). Nothing herein should be construed
          as granting, by implication, estoppel, or otherwise, any license or
          right to use the Trademarks. Use of the Trademarks as part of a link
          to or from any website is prohibited unless the establishment of such
          a link is approved in advance by us in writing. All goodwill generated
          from the use of the Trademarks inures to the benefit of us or our
          licensors.
        </p>

        <h5>
          7. Assumption of the Risk Relating to Digital Assets and Blockchain.
        </h5>
        <h6>(a) Assumption of Risks.</h6>
        <p>
          By using the Bracket Products or Services, you represent that you have
          sufficient knowledge and experience in business and financial matters,
          including a sufficient understanding of blockchain or cryptographic
          tokens and technologies and other digital assets and Rewards, storage
          mechanisms, blockchain-based software systems, and blockchain
          technology, to be able to assess and evaluate the risks and benefits
          of the Bracket Products and Services contemplated hereunder, and will
          bear the risks thereof, including loss of all amounts paid, and the
          risk that the digital assets or Rewards exchanged or earned may have
          little or no value. You acknowledge and agree that there are risks
          associated with purchasing and holding crypto or digital assets, using
          blockchain technology and staking. These include, but are not limited
          to, risk of losing access to your digital assets due to slashing, loss
          of private key(s), custodial error or purchaser error, risk of mining
          or blockchain attacks, risk of hacking and security weaknesses, risk
          of unfavorable regulatory intervention in one or more jurisdictions,
          risk related to token taxation, risk of personal information
          disclosure, risk of uninsured losses, volatility risks, and
          unanticipated risks. You acknowledge that digital assets are not (i)
          deposits of or guaranteed by a bank (ii) insured by the United States
          Federal Deposit Insurance Corporation (“FDIC”) or by any other
          governmental agency and (iii) that we do not custody and cannot
          transfer any digital assets you may interact with on while using the
          Bracket Products or Services.
        </p>
        <h6>(b) Volatility</h6>
        <p>
          As with any asset, the prices of digital assets (including Rewards)
          can increase or decrease, and there can be a substantial risk that you
          lose money buying, selling, holding, staking, or investing in digital
          assets. Fluctuations in the price of other digital assets could
          materially and adversely affect the value of your digital assets,
          which may also be subject to significant price volatility. You should
          consult your financial advisor, legal or tax professional regarding
          your specific situation and financial condition, and carefully
          consider whether trading, staking, or holding digital assets is
          suitable for you.
        </p>
        <p>
          The Company is not registered with the U.S. Securities and Exchange
          Commission or any other financial regulatory agency and does not offer
          securities services in the United States or to U.S. persons. You
          acknowledge that digital assets staked through the Bracket Products or
          Services are not subject to protections or insurance provided by the
          FDIC or the Securities Investor Protection Corporation.
        </p>
        <h6>(c) Inherent Risks</h6>
        <p>
          There are also inherent risks associated with using cryptocurrencies
          and other digital or crypto assets, including, but not limited to, the
          risk of hardware, software, and Internet connections, the risk of
          malicious software introduction, and the risk that third parties may
          obtain unauthorized access to information stored within your Wallet.
          The regulatory regime governing blockchain technologies,
          cryptocurrencies and other digital assets is uncertain, and new
          regulations or policies may materially adversely affect the potential
          utility or value of such cryptocurrencies and digital assets. There
          also exists the risks of new taxation of the purchase or sale of
          cryptocurrencies and other digital assets. ANY STORAGE, DEPOSIT,
          PURCHASE OR SALE YOU MAKE, ACCEPT OR FACILITATE, WHETHER INSIDE OR
          OUTSIDE OF THE BRACKET PRODUCTS OR SERVICES, OF A DIGITAL ASSET, WILL
          BE ENTIRELY AT YOUR RISK. THE COMPANY DOES NOT CONTROL OR ENDORSE
          PURCHASES OR SALES OF DIGITAL ASSETS OUTSIDE OF THE BRACKET PRODUCTS
          AND SERVICES.
        </p>

        <h6>(d) Use of Blockchain & Smart Contracts</h6>
        <p>
          Smart contracts execute automatically when certain conditions are met.
          Since smart contracts on Blockchain Networks typically cannot be
          stopped or reversed, vulnerabilities in their programming and design
          or other vulnerabilities that may arise due to hacking or other
          security incidents can have adverse effects to staked and restaked
          assets, including but not limited to significant volatility and risk
          of loss. The Bracket Products and Services are dependent on Blockchain
          Networks and their technologies. YOU ACCEPT AND ACKNOWLEDGE THAT WE
          WILL NOT BE RESPONSIBLE FOR ANY COMMUNICATION FAILURES, DISRUPTIONS,
          ERRORS, DISTORTIONS, OR DELAYS YOU MAY EXPERIENCE WHEN USING
          BLOCKCHAIN NETWORKS, HOWEVER CAUSED. UPGRADES TO BLOCKCHAIN NETWORKS,
          OR A CHANGE IN HOW TRANSACTIONS ARE CONFIRMED ON THE BLOCKCHAIN
          NETWORKS MAY HAVE UNINTENDED, ADVERSE EFFECTS ON ALL OTHER
          TRANSACTIONS LEVERAGING A BLOCKCHAIN NETWORK.
        </p>
        <p>
          WE ARE NOT RESPONSIBLE FOR LOSSES DUE TO BLOCKCHAIN NETWORKS, OR ANY
          ELECTRONIC WALLET, INCLUDING, BUT NOT LIMITED TO, LATE REPORTING BY
          DEVELOPERS OR REPRESENTATIVES (OR NO REPORT AT ALL) OF ANY ISSUES WITH
          THE BLOCKCHAIN NETWORKS SUPPORTING THE BRACKET PRODUCTS OR SERVICES,
          INCLUDING FORKS, TECHNICAL NODE ISSUES, OR ANY OTHER ISSUES HAVING
          FUND LOSSES AS A RESULT.
        </p>
        <p>
          WE WILL NOT BE RESPONSIBLE OR LIABLE TO YOU FOR ANY LOSSES YOU INCUR
          AS THE RESULT OF YOUR USE OF ANY BLOCKCHAIN NETWORK, OR YOUR
          ELECTRONIC WALLET, INCLUDING, BUT NOT LIMITED TO, ANY LOSSES, DAMAGES
          OR CLAIMS ARISING FROM: (I) USER ERROR, SUCH AS FORGOTTEN PASSWORDS,
          PRIVATE KEYS, OR INCORRECTLY CONSTRUED SMART CONTRACTS OR OTHER
          TRANSACTIONS; (II) SERVER FAILURE OR DATA LOSS; (III) CORRUPTED WALLET
          FILES; OR (IV) UNAUTHORIZED ACCESS OR ACTIVITIES BY THIRD PARTIES,
          INCLUDING, BUT NOT LIMITED TO, THE USE OF VIRUSES, PHISHING,
          BRUTE-FORCING, OR OTHER MEANS OF ATTACK AGAINST THE SITES, BLOCKCHAIN
          NETWORKS, OR ANY WALLET.
        </p>

        <h5>8. DISCLAIMERS.</h5>
        <p>
          ALL INFORMATION PROVIDED ON OR THROUGH THE BRACKET PRODUCTS AND
          SERVICES IS FOR INFORMATIONAL PURPOSES ONLY AND IS NOT A
          RECOMMENDATION FOR ANY INVESTMENT OR ADVICE OF ANY KIND, AND SHALL NOT
          CONSTITUTE OR IMPLY ANY OFFER TO PURCHASE, SELL OR HOLD ANY SECURITY,
          OR TO ENTER INTO OR ENGAGE IN ANY TYPE OF TRANSACTION. WE ARE NOT
          LICENSED AS BROKERS, BROKER-DEALERS, LAWYERS, ACCOUNTANTS, OR
          INVESTMENT ADVISORS, AND WE DO NOT PROVIDE INVESTMENT, LEGAL, TAX, OR
          OTHER ADVICE. YOU ACKNOWLEDGE AND UNDERSTAND THAT YOU SHOULD SEEK YOUR
          OWN PROFESSIONAL ADVISERS, INCLUDING LEGAL COUNSEL, FOR LEGAL,
          BROKERAGE, OR ACCOUNTING SERVICES AND THAT THE COMPANY’S PRODUCTS AND
          SERVICES ARE NOT A SUBSTITUTE FOR SUCH THIRD-PARTY PROFESSIONALS. WE
          MAY PROVIDE ADVICE AND PROFESSIONAL RECOMMENDATIONS TO ASSIST YOU TO
          RENDER INFORMED DECISIONS. HOWEVER, OUR SERVICES ARE NOT MEANT TO BE A
          SUBSTITUTE FOR LEGAL ADVICE OR YOUR EXERCISE OF YOUR OWN BUSINESS
          JUDGMENT. ANY SUCH JUDGMENTS OR DECISIONS ARE MADE AT YOUR SOLE
          ELECTION. WHEN OUR PRODUCTS OR SERVICES INCLUDE ESTIMATES OR
          PREDICTIONS OF FUTURE EVENTS OR BEHAVIORS, WE MAKE NO GUARANTEES AS TO
          THE OCCURRENCE OF SUCH FUTURE EVENTS OR BEHAVIORS.
        </p>
        <p>
          IN ADDITION, ANY PRICES DETERMINED BY THE COMPANY USING AN ORACLE
          FEED, CLAIMS MADE USING A KEEPER CONTRACT, OR OTHERWISE MAY BE
          INACCURATE FOR REASONS OUTSIDE OF THE COMPANY’S REASONABLE CONTROL.
          THE COMPANY IS NOT RESPONSIBLE OR LIABLE FOR INACCURATE PRICES
          RESULTING FROM DELAYS OR UNAVAILABILITIES OF ORACLE, KEEPER CONTRACTS,
          BLOCKCHAIN NETWORKS, OR OTHER FACTORS OUTSIDE OF OUR CONTROL.
        </p>
        <p>
          YOU UNDERSTAND AND AGREE THAT PURCHASING A CONTRACT THROUGH A BRACKET
          PRODUCT DOES NOT NECESSARILY ENTITLE YOU TO PURCHASE A DIGITAL ASSET
          IN THE PRICE RANGE FOR WHICH YOU PURCHASED SUCH CONTRACT. IF YOU HAVE
          PURCHASED A BRACKET PRODUCT AND THE PRICE OF THE APPLICABLE DIGITAL
          ASSET DOES NOT ENTER THE CLAIMABLE RANGE SET FORTH IN THE BRACKET
          PRODUCT, YOUR BRACKET PRODUCT WILL NOT BE USABLE, AND YOU WILL HAVE NO
          RECOURSE. ALL BRACKET PRODUCTS ARE PURCHASED AT YOUR OWN RISK.
        </p>
        <p>
          THE COMPANY PROVIDES, AND USER ACCEPTS, THE BRACKET PRODUCTS, THE
          SERVICES, AND ALL DIGITAL ASSETS OBTAINED THROUGH THE FOREGOING ON AN
          “AS IS” AND “AS AVAILABLE” BASIS WITHOUT ANY WARRANTIES OF ANY KIND,
          INCLUDING THAT THE BRACKET PRODUCTS AND SERVICES, WILL OPERATE
          ERROR-FREE, THAT THE BRACKET PRODUCTS AND SERVICES, THE SERVERS, OR
          THE CONTENT ARE FREE OF COMPUTER VIRUSES OR SIMILAR CONTAMINATION OR
          DESTRUCTIVE FEATURES. WE HEREBY DISCLAIM ANY AND ALL WARRANTIES
          RELATING TO THE BRACKET PRODUCTS AND SERVICES AND YOUR USE THEREOF,
          INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF TITLE, MERCHANTABILITY,
          NON-INFRINGEMENT OF THIRD PARTIES’ RIGHTS, AND FITNESS FOR PARTICULAR
          PURPOSE AND ANY WARRANTIES ARISING FROM A COURSE OF DEALING, COURSE OF
          PERFORMANCE, OR USAGE OF TRADE. WITHOUT LIMITING THE GENERALITY OF THE
          FOREGOING, WE DO NOT REPRESENT OR WARRANT THAT: (I) YOUR ACCESS TO OR
          USE OF THE BRACKET PRODUCTS AND SERVICES OR ANY DIGITAL ASSETS WILL
          MEET YOUR REQUIREMENTS; (II) YOUR ACCESS TO OR USE OF THE BRACKET
          PRODUCTS AND SERVICES OR ANY DIGITAL ASSETS WILL BE UNINTERRUPTED,
          TIMELY, SECURE OR FREE FROM ERROR; (III) THE BRACKET PRODUCTS,
          SERVICES, OR ANY CONTENT OR FEATURES MADE AVAILABLE ON OR THROUGH THE
          BRACKET PRODUCTS OR SERVICES, ARE FREE OF VIRUSES OR OTHER HARMFUL
          COMPONENTS; OR (IV) ANY DATA THAT YOU DISCLOSE WHEN YOU USE THE
          BRACKET PRODUCTS OR SERVICES WILL BE SECURE. SOME JURISDICTIONS DO NOT
          ALLOW THE EXCLUSION OF IMPLIED WARRANTIES IN CONTRACTS WITH CONSUMERS,
          SO SOME OR ALL OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
        </p>
        <p>
          THE DIGITAL ASSETS ABOUT WHICH INFORMATION IS PROVIDED ON THE BRACKET
          PRODUCTS AND SERVICES, AND ANY INFORMATION PROVIDED IN CONNECTION WITH
          THE BRACKET PRODUCTS AND SERVICES, ARE NOT NECESSARILY VIEWED BY THE
          ISSUER OR SPONSOR OF ANY SUCH DIGITAL ASSETS, OR THOSE BUYING OR
          SELLING THE DIGITAL ASSET, AS SECURITIES UNDER APPLICABLE LAWS. AS A
          RESULT, IT IS UNLIKELY THAT FULSOME DISCLOSURES FROM THE ISSUER OR
          SPONSOR, OR ANY EXECUTIVE OFFICER ASSOCIATED WITH THE DIGITAL ASSET OR
          RELATED PROTOCOL HAVE BEEN PROVIDED, AND OTHERS MAY HAVE BETTER OR
          MORE INFORMATION THAN THE INFORMATION MADE AVAILABLE TO YOU VIA THE
          BRACKET PRODUCTS AND SERVICES, ANY INFORMATION PROVIDED IN CONNECTION
          WITH THE BRACKET PRODUCTS AND SERVICES OR TO WHICH YOU MAY
          INDEPENDENTLY HAVE ACCESS.
        </p>
        <p>
          THESE TERMS DO NOT REPRESENT A COMPLETE STATEMENT OF RISK FACTORS
          ASSOCIATED WITH DIGITAL ASSETS THAT MAY BE PURCHASED OR TRACKED
          THROUGH THE BRACKET PRODUCTS AND SERVICES OR THAT MAY BE FEATURED OR
          DISCUSSED IN CONNECTION WITH THE BRACKET PRODUCTS AND SERVICES. YOU
          SHOULD CONSIDER THESE RISK WARNINGS CAREFULLY AND TAKE APPROPRIATE
          ADVICE BEFORE TAKING ANY DECISION TO PURCHASE OR TRADE A BRACKET
          PRODUCT.
        </p>
        <p>
          THE BRACKET PRODUCTS AND SERVICES MAY CONTAIN TECHNICAL INACCURACIES
          OR TYPOGRAPHICAL ERRORS OR OMISSIONS. UNLESS REQUIRED BY APPLICABLE
          LAWS, WE ARE NOT RESPONSIBLE FOR ANY SUCH TYPOGRAPHICAL OR TECHNICAL
          ERRORS LISTED ON THE BRACKET PRODUCTS AND SERVICES. WE RESERVE THE
          RIGHT TO MAKE CHANGES, CORRECTIONS, AND/OR IMPROVEMENTS TO THE BRACKET
          PRODUCTS AND SERVICES AT ANY TIME WITHOUT NOTICE.
        </p>
        <p>
          YOU ACCEPT THE INHERENT SECURITY RISKS OF PROVIDING INFORMATION AND
          DEALING ONLINE OVER THE INTERNET AND AGREE THAT WE HAVE NO LIABILITY
          OR RESPONSIBILITY FOR ANY BREACH OF SECURITY UNLESS IT IS DUE TO OUR
          GROSS NEGLIGENCE OR WILLFUL MISCONDUCT.
        </p>
        <p>
          WE PROVIDE A TECHNOLOGY PLATFORM THAT CONNECTS BUYERS AND SELLERS OF
          DIGITAL ASSETS AND FACILITATES TRANSACTIONS BETWEEN THEM. WE ARE NOT A
          BROKER, FINANCIAL INSTITUTION, OR CREDITOR. WE DO NOT AND CANNOT
          CONTROL THE ACTIONS OR OMISSIONS OF ANY INDIVIDUAL WITH WHOM YOU DEAL
          ON THE BRACKET PRODUCTS AND SERVICES. YOUR INTERACTIONS AND
          TRANSACTIONS WITH ANY OTHER USER THROUGH THE BRACKET PRODUCTS AND
          SERVICES ARE SOLELY BETWEEN YOU AND THAT USER, AND WE ARE NOT A PARTY
          TO ANY SUCH INTERACTION OR TRANSACTION. WE HEREBY DISCLAIM ANY AND ALL
          LIABILITY FOR ANY ACT OR OMISSION OF ANY USER OF THE BRACKET PRODUCTS
          AND SERVICES, AND ALL LIABILITY IN CONNECTION WITH ALL INTERACTIONS OR
          TRANSACTIONS MADE BETWEEN OR AMONG ANY OTHER USERS OF THE BRACKET
          PRODUCTS AND SERVICES. IF YOU HAVE AN ISSUE WITH ANY INTERACTION OR
          TRANSACTION THAT YOU ENTERED INTO WITH ANOTHER USER OF THE BRACKET
          PRODUCTS AND SERVICES, YOU MUST ADDRESS THAT ISSUE DIRECTLY WITH THE
          OTHER USER AND NOT WITH US – WE HAVE NO OBLIGATION TO ASSIST IN ANY OF
          THESE DISPUTES.
        </p>

        <h5>9. Limitation of Liability for Damages.</h5>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN CONNECTION WITH ANY
          WARRANTY, CONTRACT, OR COMMON LAW TORT CLAIMS, (I) IN NO EVENT SHALL
          THE COMPANY BE LIABLE TO USER FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          PUNITIVE, OR CONSEQUENTIAL DAMAGES, LOST PROFITS, BUSINESS
          INTERRUPTION LOSSES, OR LOSS OF DATA, RESULTING FROM THIS AGREEMENT,
          ACTIVITY AT THE BRACKET PRODUCTS OR SERVICES, OR ACTIONS BY THIRD
          PARTIES, REGARDLESS OF THE THEORY OF LIABILITY, INCLUDING EXPRESS
          CONTRACT, IMPLIED CONTRACT, NEGLIGENCE, WARRANTY, OR
          MISREPRESENTATION, AND WHETHER OR NOT WE ARE ADVISED OF THE
          POSSIBILITY OF SUCH DAMAGES, AND (II) DIRECT DAMAGES ARE LIMITED TO
          THE AMOUNT OF THE PURCHASE PRICE PAID BY USER TO THE COMPANY FOR THE
          DIGITAL ASSET THAT IS THE SUBJECT OF ANY USER CLAIM.
        </p>
        <p>
          ANY CLAIM RELATED TO THE BRACKET PRODUCTS AND SERVICES OR ANY DIGITAL
          ASSET PURCHASED USING THEM BROUGHT UNDER THIS AGREEMENT MUST BE
          BROUGHT WITHIN ONE (1) YEAR OF THE EVENT THAT IS THE SUBJECT OF SUCH
          CLAIM.
        </p>
        <p>
          SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES.
          THEREFORE, SOME OF THE ABOVE LIMITATIONS ON WARRANTIES IN THIS SECTION
          MAY NOT APPLY TO YOU.
        </p>
        <p>
          NOTHING IN THESE TERMS OF SERVICE SHALL AFFECT ANY NON-WAIVABLE
          STATUTORY RIGHTS THAT APPLY TO YOU.
        </p>

        <h5>10. Accessibility; External Sites.</h5>
        <p>
          We may use third parties for services relating to the technology used
          to receive, store, and transmit data (such as server operations,
          hosting, maintenance, support, upgrading, and repair). Servers
          relating to the functioning of the Bracket Products and Services may
          occasionally be inaccessible due to repair, maintenance, upgrades,
          power sources, and other factors. Information transmitted by Users may
          be sent over an unsecured connection to an email service provider.
        </p>
        <p>
          The Bracket Products and Services may contain links to third-party
          websites (“External Sites”). These links are provided solely as a
          convenience to you and not as an endorsement by us of the content or
          products available through such External Sites. The External Sites may
          offer third-party goods and services (the “Third Party Products”), and
          you acknowledge and agree that: (i) we do not manufacture or sell the
          Third Party Products and thus have no control over the quality,
          safety, legality or efficacy of any Third Party Product; (ii) we have
          no involvement in any transaction involving any Third Party Products;
          and (iii) we shall have no liability with respect to any Third Party
          Product or any transaction or interaction between you and the provider
          of any Third Party Product.
        </p>
        <p>
          The content of such External Sites is developed and provided by
          others. You should contact the site administrator or webmaster for
          those External Sites if you have any concerns regarding such links or
          any content located on such External Sites. We are not responsible for
          the content of any linked External Sites and do not make any
          representations regarding the content or accuracy of materials on such
          External Sites. You should take precautions when downloading files
          from all websites to protect your computer from viruses and other
          destructive programs. If you decide to access linked External Sites,
          you do so at your own risk.
        </p>

        <h5>11. Suspension or Termination of Products and Services.</h5>
        <p>
          You agree that we have the right to immediately suspend, pause or
          cancel your access to the Bracket Products or Services if we suspect,
          in our sole discretion, that (i) your access is being used for money
          laundering or any illegal activity or otherwise in violation of our
          Community Guidelines; (ii) you have concealed or provided false
          identification information or other details; (iii) you have engaged in
          fraudulent activity; (iv) you have acquired Digital Assets using
          inappropriate methods (including but not limited to using stolen funds
          or payments methods, or attempting to chargeback your payment while
          retaining or disposing of a Digital Asset); (v) you are using,
          employing, or operating bots or other forms of automation and/or
          multiple accounts to engage in any activity on a Bracket Product or
          Service; or (vi) you have otherwise acted fraudulently or in violation
          of this Agreement.
        </p>
        <p>
          IF WE HAVE A REASONABLE BELIEF THAT YOU HAVE ENGAGED IN ACTIVITIES (i)
          THROUGH (vi) ABOVE, THE COMPANY RESERVES THE RIGHT, IN OUR SOLE
          DISCRETION, TO CLOSE AND/OR EXPIRE DERIVATIVE CONTRACTS. IN SOME CASES
          WHERE WE SUSPECT FRAUD OR RISK ASSOCIATED WITH YOUR USE OF THE BRACKET
          PRODUCTS OR SERVICES, WE MAY CHOOSE, IN OUR SOLE DISCRETION, TO CLOSE
          AND/OR EXPIRE DERIVATIVE CONTRACTS IN OUR SOLE, ABSOLUTE DISCRETION.
        </p>
        <p>
          Except as expressly set forth in this Section, the Company has no
          control over, and no ability to control, your Wallet.
        </p>

        <h5>12. Digital Millennium Copyright Act</h5>
        <p>Reporting Claims of Copyright Infringement</p>
        <p>
          We respect the intellectual property rights of others and attempt to
          comply with all relevant laws. We will review all claims of copyright
          infringement received and remove any content deemed to have been
          posted or distributed in violation of any such laws. Our designated
          agent under the Digital Millennium Copyright Act (the “Act”) for the
          receipt of any Notification of Claimed Infringement which may be given
          under that Act is as follows:
        </p>

        <a href="mailto:legal@bracket.fi">legal@bracket.fi</a>
        <br />
        <p>
          If you believe that your work has been copied on the Bracket Products
          or Services in a way that constitutes copyright infringement, please
          provide our agent with notice in accordance with the requirements of
          the Act, including (i) a description of the copyrighted work that has
          been infringed and the specific location on the Bracket Products or
          Services where such work is located; (ii) a description of the
          location of the original or an authorized copy of the copyrighted
          work; (iii) your address, telephone number and e-mail address; (iv) a
          statement by you that you have a good faith belief that the disputed
          use is not authorized by the copyright owner, its agent or the law;
          (v) a statement by you, made under penalty of perjury, that the
          information in your notice is accurate and that you are the copyright
          owner or authorized to act on the copyright owner’s behalf; and (vi)
          an electronic or physical signature of the owner of the copyright or
          the person authorized to act on behalf of the owner of the copyright
          interest.
        </p>
        <h6>Counter-Notification Procedures</h6>
        <p>
          If you believe that material you posted on the Bracket Products or
          Services was removed or access to it was disabled by mistake or
          misidentification, you may file a counter-notification with us (a
          “Counter-Notice”) by submitting written notification to our copyright
          agent (identified above).
        </p>
        <p>
          Pursuant to the Act, the Counter-Notice must include substantially the
          following: (i) your physical or electronic signature; (ii) an
          identification of the material that has been removed or to which
          access has been disabled and the location at which the material
          appeared before it was removed or access disabled; (iii) adequate
          information by which we can contact you (including your name, postal
          address, telephone number, and, if available, email address); (iv) a
          statement under penalty of perjury by you that you have a good faith
          belief that the material identified above was removed or disabled as a
          result of a mistake or misidentification of the material to be removed
          or disabled; (v) a statement that you will consent to the jurisdiction
          of the Federal District Court for the judicial district in which your
          address is located (or if you reside outside the United States for any
          judicial district in which the Bracket Products or Services may be
          found) and that you will accept service from the person (or an agent
          of that person) who provided the Bracket Products or Services with the
          complaint at issue.
        </p>
        <p>
          The Act allows us to restore the removed content if the party filing
          the original notice does not file a court action against you within
          ten business days of receiving the copy of your Counter-Notice.
        </p>
        <p>
          Please be aware that if you knowingly materially misrepresent that
          material or activity on the Bracket Products or Services was removed
          or disabled by mistake or misidentification, you may be held liable
          for damages (including costs and attorneys’ fees) under Section 512(f)
          of the Act.
        </p>

        <h5>13. Promotions</h5>
        <p>
          The Company may offer certain promotions, points, and rewards
          (collectively “Rewards”) programs from time to time, which may be
          subject to separate additional terms and condition. Rewards are not,
          and may never convert to, accrue to, be used as a basis to calculate,
          or become any other tokens or virtual assets or distribution thereof.
          Rewards may be virtual items with no monetary value. Rewards do not
          constitute any currency or property of any type and are not
          redeemable, refundable, or eligible for any fiat or virtual currency
          or anything else of value. Rewards are not transferable between users,
          and you may not attempt to sell, trade, or transfer any Rewards, or
          obtain any manner of credit using any Rewards. Any attempt to sell,
          trade, or transfer any Rewards or tokens redeemable for or
          representing any Rewards will be null and void. If you choose to
          participate in any of our promotions, rewards programs, or challenge
          opportunities, you agree that in order to grant you Rewards we may
          require additional information from you, including, without
          limitation, personally identifiable information that we may use to
          report rewards issued to applicable governmental authorities as
          required (such as for tax purposes). Our collection, use, and
          disclosure of such personally identifiable information shall at all
          times be subject to the terms and conditions of our Privacy Policy.
        </p>
        <p>
          ALL REWARDS ARE PROVIDED BY THE COMPANY ON AN “AS IS” BASIS. THE
          COMPANY DOES NOT MAKE, AND EXPRESSLY DISCLAIMS, ANY WARRANTY, WHETHER
          EXPRESS, IMPLIED OR STATUTORY, REGARDING THE REWARDS, INCLUDING,
          WITHOUT LIMITATION, ANY IMPLIED OR STATUTORY WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR
          NON-INFRINGEMENT.
        </p>
        <p>
          Please be advised that by accepting any Rewards you acknowledge and
          agree that you may be liable to pay taxes on such Rewards. It is your
          responsibility to determine your tax liabilities and to comply with
          applicable law. Consult with your tax professional regarding potential
          tax implications related to Rewards.
        </p>

        <h5>14. User Responsibility and Indemnification.</h5>
        <p>
          You acknowledge and agree that your access to the Bracket Products and
          Services are based upon the truth of the promises, statements, and
          representations made in this Agreement. You are solely responsible for
          any costs, expenses, and damages arising from breaches of this
          Agreement or third-party assertions inconsistent with User promises,
          statements, representations, and warranties. This obligation survives
          termination of this Agreement. You agree to use your best efforts to
          assist the Company in the investigation and resolution of any
          third-party claim(s) or assertion inconsistent with User’s obligations
          under this Agreement, at no charge and promptly upon receipt of notice
          from the Company of such claim or assertion.
        </p>
        <p>
          You agree to defend, indemnify, and hold us and our officers,
          directors, employees, successors, licensees and assigns harmless from
          and against any claims, actions, or demands, including, without
          limitation, reasonable legal and accounting fees, arising or resulting
          from (i) breach of any of your representations, warranties, covenants,
          or other obligations under these Terms; (ii) your misuse of the
          Bracket Products or Services, or any digital assets; (iii) your
          negligence or misconduct; (iv) any transaction that you enter into
          with another User of the Bracket Products or Services; or (v) any
          actual or alleged infringement or misappropriation of third party
          intellectual property rights by any content provided by you to the
          Company. We shall provide notice to you of any such claim, suit, or
          proceeding and shall assist you, at your expense, in defending any
          such claim, suit, or proceeding. We reserve the right, at your
          expense, to assume the exclusive defense and control of any matter
          that is subject to indemnification under this section. In such case,
          you agree to cooperate with any reasonable requests assisting our
          defense of such matter.
        </p>

        <h5>15. Communications with Us.</h5>
        <p>
          Although we encourage you to email us, we do not want you to, and you
          should not, email us any content that contains confidential
          information. With respect to all emails and communications you send to
          us, including, but not limited to, feedback, questions, comments,
          suggestions, and the like, we shall be free to use any ideas,
          concepts, know-how, or techniques contained in your communications for
          any purpose whatsoever, including but not limited to, the development,
          production, and marketing of products and services that incorporate
          such information without compensation or attribution to you.
        </p>

        <h5>16. Compliance with Applicable Laws.</h5>
        <p>
          The Bracket Products and Services are based in Panama City. However,
          Restricted Users are not permitted to access and use the Bracket
          Products or Services, including US and Panamanian Persons. Your access
          to the Bracket Products and Services from outside of Panama is at your
          own risk, and at all times, you are solely responsible for ensuring
          compliance with the laws of your specific jurisdiction in connection
          with your access to and use of the Bracket Products and Services.
        </p>

        <h5>17. Notices; Electronic Communications.</h5>
        <p>
          We may provide User with notices by email using the information
          provided by User to us, or by postings to the Bracket Products or
          Services. Notice is deemed given upon the earlier of (a) actual
          receipt, (b) twenty-four (24) hours after an email is sent, or (c)
          five (5) calendar days after a notice is posted to the Bracket
          Products. User stipulates to electronic communications as the sole
          method of notice and communications, including service of legal
          process. During the term of this Agreement it is possible that the
          purchase, sale, use, and transfer of ownership of digital Assets may
          become subject to one or more laws not in effect as of the effective
          date hereof, or a change in the ownership of intellectual property
          rights of our licensors relevant to the Content. Such new laws, if
          any, or change of ownership, may fundamentally alter the power of the
          Company to grant the rights above or the exercise of such rights by
          User. In such case, we may update our Terms.
        </p>

        <h5>18. Privacy.</h5>
        <p>
          Account information provided by User, and data about User generated in
          connection with this Agreement and User activities related to the
          Bracket Products or Services containing personally identifiable
          information, are subject to our Privacy Policy.
        </p>

        <h5>19. Entire Agreement.‍‍</h5>
        <p>
          This Agreement incorporates by reference all terms appearing at the
          links identified in these is Agreement. The Agreement may be updated
          and modified by us from time to time. Those modifications are
          incorporated as part of the Agreement. If User does not accept the
          modifications to the Agreement, its sole remedy is to conclude its use
          of the Bracket Products and Services. By continuing to access or use
          the Bracket Products and Services after such modifications are made,
          you confirm your acceptance of the modified Agreement and all of the
          terms incorporated therein by reference effective as of the date these
          Agreement is modified. It is your sole responsibility to review the
          Agreement from time to time to view such changes and to ensure that
          you understand the terms and conditions that apply when you access or
          use the Bracket Products or Services.
        </p>
        <p>
          All provisions which, by their nature, are intended to survive
          termination shall continue, including, without limitation, sections
          pertaining to equitable remedies, binding arbitration, class action,
          indemnification, and limitation of liability for damages.
        </p>

        <h5>20. Binding Arbitration.</h5>
        <p>
          Subject to Section 21 below, in the event of a dispute arising under
          or relating to this Agreement (“Dispute”), you agree to first contact
          the Company and attempt to resolve the Dispute informally by sending a
          written notice of your Dispute to the Company by email at
          legal@bracket.fi (“Notice”). The Notice must (a) include your name,
          residence address, email address, and telephone number; (b) describe
          the nature and basis of the Dispute; and (c) set forth the specific
          relief sought. We will respond to any such Notice as soon as
          reasonably practicable after receipt. If you and the Company cannot
          reach an agreement to resolve the Dispute within ninety (90) calendar
          days after our receipt of such Notice, then either party may elect to
          finally and exclusively resolve the Dispute by binding arbitration
          governed by the Federal Arbitration Act (“FAA”). Any election to
          arbitrate, at any time, shall be final and binding on the other party.
        </p>

        <p>
          IF EITHER PARTY CHOOSES ARBITRATION, NEITHER PARTY SHALL HAVE THE
          RIGHT TO LITIGATE SUCH CLAIM IN COURT OR TO HAVE A JURY TRIAL, EXCEPT
          EITHER PARTY MAY BRING ITS CLAIM IN ITS LOCAL SMALL CLAIMS COURT, IF
          PERMITTED BY THAT SMALL CLAIMS COURT RULES AND IF WITHIN SUCH COURT’S
          JURISDICTION. ARBITRATION IS DIFFERENT FROM COURT, AND DISCOVERY AND
          APPEAL RIGHTS MAY ALSO BE LIMITED IN ARBITRATION.
        </p>
        <p>
          All Disputes will be resolved before a neutral arbitrator selected
          jointly by the parties, whose decision will be final, except for a
          limited right of appeal under the FAA. The arbitration shall be
          commenced and conducted by JAMS pursuant to its then-current
          Comprehensive Arbitration Rules and Procedures and in accordance with
          the Expedited Procedures in those rules or, where appropriate,
          pursuant to JAMS’ Streamlined Arbitration Rules and Procedures. All
          applicable JAMS’ rules and procedures are available at the JAMS
          website www.jamsadr.com. Each party will be responsible for paying any
          JAMS filing, administrative and arbitrator fees in accordance with
          JAMS rules. Judgment on the arbitrator’s award may be entered in any
          court having jurisdiction. This clause shall not preclude parties from
          seeking provisional remedies in aid of arbitration from a court of
          appropriate jurisdiction. The arbitration may be conducted in person,
          through the submission of documents, by phone, or online. If conducted
          in person, the arbitration shall take place in Panama City, Panama.
          The parties may litigate in court to compel arbitration, to stay a
          proceeding pending arbitration, or to confirm, modify, vacate, or
          enter judgment on the award entered by the arbitrator. The parties
          shall cooperate in good faith in the voluntary and informal exchange
          of all non-privileged documents and other information (including
          electronically stored information) relevant to the Dispute immediately
          after commencement of the arbitration.
        </p>

        <h5>21. Class Action Waiver.</h5>
        <p>
          You agree that any arbitration or proceeding shall be limited to the
          Dispute between us and you individually. To the full extent permitted
          by Law, (i) no arbitration or proceeding shall be joined with any
          other; (ii) there is no right or authority for any Dispute to be
          arbitrated or resolved on a class action-basis or to utilize class
          action procedures; and (iii) there is no right or authority for any
          Dispute to be brought in a purported representative capacity on behalf
          of the general public or any other persons. YOU AGREE THAT YOU MAY
          BRING CLAIMS AGAINST US ONLY IN YOUR INDIVIDUAL CAPACITY AND NOT AS A
          PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE
          PROCEEDING.
        </p>

        <h5>22. Equitable Relief.</h5>
        <p>
          You acknowledge and agree that in the event of a breach or threatened
          violation of our intellectual property rights or confidential and
          proprietary information by You, we will suffer irreparable harm and
          will therefore be entitled to injunctive relief to enforce this
          Agreement. We may, without waiving any other remedies under this
          Agreement, seek from any court having jurisdiction any interim,
          equitable, provisional, or injunctive relief that is necessary to
          protect our rights and property pending the outcome of the arbitration
          referenced above. You hereby irrevocably and unconditionally consent
          to the personal and subject matter jurisdiction of the courts of
          Panama City for purposes of any such action by us.
        </p>

        <h5>23. General Terms.</h5>
        <p>
          This Agreement shall be governed by and construed in accordance with
          the laws of Panama without regard to conflict of laws principles. We
          reserve the right, in our sole discretion, to terminate this Agreement
          and/or restrict, suspend, or terminate your access to all or any part
          of the Bracket Products or Services, at any time and for any reason
          without prior notice or liability. We reserve the right to change,
          suspend, or discontinue all or any part of the Bracket Products or
          Services at any time without prior notice or liability.
        </p>
        <p>
          No waiver by the Company shall be implied. A waiver must be in writing
          and signed by an officer of the Company. If any portion of this
          Agreement is found to be unenforceable, such portion will be modified
          to reflect the parties’ intent set forth in such portion and only to
          the extent necessary to make it enforceable. The remaining provisions
          of this Agreement will remain in full force and effect. The Company
          may assign, subcontract, delegate, and transfer its rights and
          obligations under this Agreement to a third party. User represents he
          or she has the legal power to enter into this Agreement. This
          Agreement is binding upon the heirs, personal representatives,
          successors, and assigns of User. The section headings herein are
          provided merely for convenience and shall not be given any legal
          import.
        </p>
        <p>
          We have the right, but not the obligation, to review and reject or
          remove content that, in our sole discretion, does not adhere to these
          Terms. We also reserve the right, in our sole and absolute discretion,
          to deny you access to any website, or to any portion of any website,
          for any reason with or without notice. We may, at our sole and
          absolute discretion, without notice or liability to you, and without
          limiting any of our other rights or remedies at law or in equity,
          immediately suspend or terminate your user account.
        </p>
      </section>
    </div>
  );
};
